/**
 * Who is signed in, for the screens that sit too deep to be handed the user.
 *
 * Every section already takes `user` as a prop from `CrmLayout` and that stays
 * the right way for a SECTION to read it. This exists for the tab five levels
 * below one, which needs the operator for a single permission check and nothing
 * else. The project section is the case that forced it: the section door answers
 * to `manage:crm.projects`, while the "invoice time" button on its time tab
 * creates an INVOICE and therefore answers to `manage:crm.sales` - a right a
 * project manager can perfectly well be without. So the check cannot be lifted
 * to the door, and threading `user` through ProjectsSection, ProjectDetail and
 * TimeEntries to reach it would put a prop on three components that have no use
 * for it. That is how a prop ends up passed everywhere and read nowhere, and how
 * the next person to add a tab forgets to pass it.
 *
 * NOT nullable, unlike `useSuppressedNumbers` in the telephony folder. That one
 * is optional because a screen mounted outside the shell can sensibly strike
 * nothing through; there is no equivalent sensible answer to "who is this", and
 * a hook that returned null would let a caller quietly hide a button from
 * somebody who is allowed to press it. `CrmLayout` mounts the provider above
 * every route, so a missing context is a wiring mistake and says so out loud.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { StaffUser } from "../../lib/auth";

const CrmUserContext = createContext<StaffUser | null>(null);

export function CrmUserProvider({ user, children }: { user: StaffUser; children: ReactNode }) {
  // No memo and no wrapper object: the value IS the user, one object held by
  // `main.tsx` for the whole session, so the context changes identity exactly
  // when the operator does - which is the one moment consumers must re-render.
  return <CrmUserContext.Provider value={user}>{children}</CrmUserContext.Provider>;
}

export function useCrmUser(): StaffUser {
  const user = useContext(CrmUserContext);
  if (!user) throw new Error("useCrmUser: no CrmUserProvider above this component");
  return user;
}
